import axios from 'axios'
import router from '@/router'
import qs from 'qs'
import { useMainStore } from '@/store'
import paths from '@/router/paths';

export default interface IApiResponse {
  status: boolean;
  data: any;
  access_token?: string;
  next_page_url?: string;
  per_page?: number;
  total?: number;
  message?: string;
}

export enum Endpoint {
  login = 'login',
  users = 'users',
  passwordRecovery = "password/email",
  validatePrescription = "validate-recipe",
  prescriptions = "recipes-pharmacy",
  dismissMedicine = "drugs-dismissed",
  extraMedicines = "recipes-extra-drugs",
  singleMedicinePDF = 'pdf-drugs-individual',
}

export default class NetworkService {
  public baseUrl = process.env.VUE_APP_API_BASE_URL
  constructor() { }
  async post<T>(endPoint: Endpoint | string, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.post(this.baseUrl + endPoint, params, { headers: this.headers() })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }
  async get(endPoint: Endpoint | string, query: any = {}, defaultToken = ''): Promise<IApiResponse> {
    if (query.search) {
      query.q = query.search.trim()
    }
    if (query.orderBy && query.orderBy.length) {
      // query.orderBy = `${query.orderBy[0].sortName}|${query.orderBy[0].order}`
      query.orderByDirection = query.orderBy[0].order // asc ou desc
      query.orderBy = query.orderBy[0].sortName // nome da coluna
    }
    delete (query.search)
    const url = `${this.baseUrl}${endPoint}?${qs.stringify(query)}`

    try {
      const res = await axios.get(url, { headers: this.headers('', defaultToken) })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async put<T>(endPoint: Endpoint | string, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.put(this.baseUrl + endPoint, params, { headers: this.headers() })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async delete(endPoint: Endpoint | string): Promise<IApiResponse> {
    try {
      const res = await axios.delete(this.baseUrl + endPoint, { headers: this.headers() })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async postEncoded<T>(endPoint: Endpoint, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.post(this.baseUrl + endPoint, qs.stringify(params), { headers: this.headers('urlencoded') })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async putEncoded<T>(endPoint: Endpoint, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.put(this.baseUrl + endPoint, qs.stringify(params), { headers: this.headers('urlencoded') })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async postMultipart<T>(endPoint: Endpoint | string, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.post(this.baseUrl + endPoint, this.makeItMultipartParams(params), { headers: this.headers('multipart') })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async putMultipart<T>(endPoint: Endpoint | string, params: T): Promise<IApiResponse> {
    try {
      const res = await axios.put(this.baseUrl + endPoint, this.makeItMultipartParams(params), { headers: this.headers('multipart') })
      return this.handleResponse(res)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  makeItMultipartParams(params: any): FormData {
    const form = new FormData()
    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        params[key].map((r: any) => {
          form.append(`${key}[]`, r)
        })
      } else {
        form.append(key, params[key])
      }
    })
    return form
  }

  makeExternalRequest(type: string, endPoint: string, params: any, headers = {}) {
    switch (type) {
      case 'post':
        return axios.post(endPoint, params, { headers })

      case 'get':
        return axios.get(endPoint, { headers })

      case 'put':
        return axios.put(endPoint, params, { headers })
    }
  }

  headers(encodeType = '', defaultToken = '') {
    const store = useMainStore()
    const access_token = store.token || defaultToken
    const headers: any = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }

    // headers['UserInfo'] = JSON.stringify(this.getDeviceInfo())

    if (encodeType == 'multipart') {
      delete (headers['Content-Type'])
    }
    if (encodeType == 'urlencoded') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }
    if (access_token) {
      headers.Authorization = `Bearer ${access_token}`
    }
    return headers
  }

  handleResponse(res: any) {
    if (res.data.status === false) {
      this.handleError(res)
    } else {
      return res.data
    }
  }

  handleError(err: any) {
    const error = err.response || err
    if (error && error.status && error.status == 401) {
      router.push(paths.login)
    }
    let message = error?.data?.message || error?.message || 'Ocorreu um erro, tente novamente'
    if (error?.data?.errors) {
      const keys = Object.keys(error.data?.errors)
      message = error.data?.errors[keys[0]][0]
    }
    throw ({ message })
  }

  getDeviceInfo = (pipelize = false) => {
    const toPipe = (info: any) => {
      return `${info.os}|${info.app}|no-model|${info.appVersion}|no-os-version`;
    };

    const info = {
      os: navigator.userAgent,
      appVersion: process.env.VUE_APP_VERSION_STRING,
      app: "diag-validator",
    };

    return pipelize ? toPipe(info) : info;
  }
}
